import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, theme, Drawer, Dropdown } from 'antd';
import {
  DashboardOutlined,
  HomeOutlined,
  TeamOutlined,
  FileOutlined,
  BarChartOutlined,
  MenuOutlined,
  ThunderboltOutlined,
  ContainerOutlined,
  CalculatorOutlined,
  FileTextOutlined,
  ContactsOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  HistoryOutlined,
  PhoneOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import permissionService from '../services/permissionService';
import ChangePasswordModal from '../components/common/ChangePasswordModal';

const { Header, Content, Sider } = Layout;

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(window.innerWidth <= 768);
  const [mobileSiderVisible, setMobileSiderVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [changePasswordModalVisible, setChangePasswordModalVisible] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const { token } = theme.useToken();

  // Load permissions khi component mount
  useEffect(() => {
    const loadPermissions = async () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Nếu chưa có permissions, load từ API
      if (!user.permissions || !Array.isArray(user.permissions) || user.permissions.length === 0) {
        await permissionService.loadUserPermissions();
      }
      setPermissionsLoaded(true);
    };
    loadPermissions();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth <= 768) {
        setCollapsed(true);
        setMobileSiderVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSider = () => {
    if (windowWidth <= 768) {
      setMobileSiderVisible(!mobileSiderVisible);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleLogout = () => {
    // Xóa token và thông tin người dùng khỏi localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Chuyển hướng về trang đăng nhập
    navigate('/login');
  };

  const handleChangePassword = () => {
    setChangePasswordModalVisible(true);
  };

  const handleChangePasswordSuccess = () => {
    setChangePasswordModalVisible(false);
  };

  // Tính toán menu items dựa trên permissions (sử dụng useMemo để re-render khi permissions được load)
  const menuItems = useMemo(() => [
    {
      key: "/",
      icon: <DashboardOutlined />,
      label: <Link to="/">Tổng quan</Link>,
      show: permissionService.checkPermission('view_dashboard')
    },
    {
      key: "/factories",
      icon: <HomeOutlined />,
      label: <Link to="/factories">Danh sách nhà xưởng</Link>,
      show: permissionService.checkPermission('view_factories')
    },
    {
      key: "/tenants",
      icon: <TeamOutlined />,
      label: <Link to="/tenants">Khách thuê</Link>,
      show: permissionService.checkPermission('view_tenants')
    },
    {
      key: "/contracts",
      icon: <TeamOutlined />,
      label: <Link to="/contracts">Danh sách hợp đồng</Link>,
      show: permissionService.checkPermission('view_contracts')
    },
    {
      key: "/rent-invoices",
      icon: <FileTextOutlined />,
      label: <Link to="/rent-invoices">Quản lý hóa đơn</Link>,
      show: permissionService.checkPermission('view_rent_invoices')
    },
    {
      key: "/supports",
      icon: <ContactsOutlined />,
      label: <Link to="/supports">Hỗ trợ & Ý kiến</Link>,
      show: false // hidden as requested
    },
    {
      key: "/notifications",
      icon: <BellOutlined />,
      label: <Link to="/notifications">Thông báo tuỳ chỉnh</Link>,
      show: permissionService.checkPermission('view_notifications')
    },
    {
      key: "/banners-and-popups",
      icon: <PictureOutlined />,
      label: <Link to="/banners-and-popups">Banner & Pop-up App</Link>,
      show: permissionService.checkPermission('view_notifications') || permissionService.checkPermission('view_admins') || true
    },
    {
      key: "/reports",
      icon: <BarChartOutlined />,
      label: <Link to="/reports">Báo cáo</Link>,
      show: permissionService.checkPermission('view_reports')
    },
    {
      key: "/admins",
      icon: <UserOutlined />,
      label: <Link to="/admins">Quản lý tài khoản</Link>,
      show: permissionService.checkPermission('view_admins')
    },
    {
      key: "/user-activities",
      icon: <HistoryOutlined />,
      label: <Link to="/user-activities">Lịch sử hoạt động</Link>,
      show: permissionService.checkPermission('view_user_activities')
    },
    {
      key: "/utilities",
      icon: <SettingOutlined />,
      label: <Link to="/utilities">Quản lý tiện ích</Link>,
      show: permissionService.checkPermission('view_utilities') || permissionService.checkPermission('view_admins')
    },
    {
      key: "/services",
      icon: <SettingOutlined />,
      label: <Link to="/services">Quản lý dịch vụ</Link>,
      show: permissionService.checkPermission('view_services') || permissionService.checkPermission('view_admins')
    },
    {
      key: "/support-call-logs",
      icon: <PhoneOutlined />,
      label: <Link to="/support-call-logs">Lịch sử liên hệ hỗ trợ</Link>,
      show: permissionService.checkPermission('view_admins')
    }
  ].map(item => {
    // Nếu item có children, filter children trước
    if (item.children && Array.isArray(item.children)) {
      const filteredChildren = item.children.filter(child => child.show);
      // Chỉ hiển thị parent nếu có ít nhất một child được hiển thị và parent.show = true
      return {
        ...item,
        children: filteredChildren.length > 0 ? filteredChildren : undefined,
        show: item.show && filteredChildren.length > 0
      };
    }
    return item;
  }).filter(item => item.show), [permissionsLoaded]);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Đồng bộ state selected/open theo URL để tránh select nhầm khi keys trùng hoặc defaultSelectedKeys cố định.
  const selectedMenuKey = useMemo(() => {
    const path = location.pathname || '/';
    // normalize: nếu route có dạng /reports/xxx thì chọn /reports
    const candidates = [
      '/',
      '/factories',
      '/tenants',
      '/contracts',
      '/rent-invoices',
      '/supports',
      '/notifications',
      '/banners-and-popups',
      '/reports',
      '/admins',
      '/user-activities',
    ];
    const matched = candidates
      .filter(p => p === '/' ? path === '/' : path.startsWith(p))
      .sort((a, b) => b.length - a.length)[0];
    return matched || path;
  }, [location.pathname]);

  const openMenuKeys = useMemo(() => {
    if (['/contracts', '/rent-invoices'].includes(selectedMenuKey)) return ['contracts'];
    return [];
  }, [selectedMenuKey]);

  // Cho phép người dùng tự xổ/đóng submenu (controlled openKeys phải có onOpenChange).
  const [openKeys, setOpenKeys] = useState(openMenuKeys);

  // Khi đổi route, tự mở đúng parent menu tương ứng.
  useEffect(() => {
    setOpenKeys(openMenuKeys);
  }, [openMenuKeys]);

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <SettingOutlined />,
      label: 'Đổi mật khẩu',
      onClick: handleChangePassword,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      onClick: handleLogout,
    },
  ];

  const renderMenu = () => (
    <Menu
      mode="inline"
      selectedKeys={[selectedMenuKey]}
      openKeys={openKeys}
      onOpenChange={(keys) => setOpenKeys(keys)}
      style={{
        height: '100%',
        borderRight: 0,
        paddingTop: token.padding
      }}
      items={menuItems}
      onClick={() => isMobile && setMobileSiderVisible(false)}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        padding: '0 16px',
        background: token.colorBgContainer,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'fixed',
        width: '100%',
        zIndex: 1000,
        boxShadow: token.boxShadow
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center'
        }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={toggleSider}
            style={{ marginRight: 16, fontSize: '16px' }}
          />
          <img src={require('../assets/images/logo.png')} alt="logo" style={{ width: 30, height: 30, marginRight: 10 }} />
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '18px' : isTablet ? '20px' : '24px',
            color: token.colorTextHeading
          }}>
            Quản Lý Nhà Xưởng
          </h1>
        </div>
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            type="text"
            icon={<UserOutlined />}
            style={{ fontSize: '16px' }}
          >
            {!isMobile && 'Tài khoản'}
          </Button>
        </Dropdown>
      </Header>

      {isMobile ? (
        <Drawer
          placement="left"
          onClose={() => setMobileSiderVisible(false)}
          open={mobileSiderVisible}
          width={250}
          bodyStyle={{ padding: 0 }}
          headerStyle={{ display: 'none' }}
        >
          {renderMenu()}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 64,
            bottom: 0,
          }}
          width={250}
        >
          {renderMenu()}
        </Sider>
      )}

      <Layout style={{
        marginLeft: isMobile ? 0 : (collapsed ? 80 : 250),
        marginTop: 64,
        transition: 'all 0.2s'
      }}>
        <Content>
          <Outlet />
        </Content>
      </Layout>

      <ChangePasswordModal
        visible={changePasswordModalVisible}
        onCancel={() => setChangePasswordModalVisible(false)}
        onSuccess={handleChangePasswordSuccess}
      />
    </Layout>
  );
}

export default MainLayout; 